/**
 * Inter-client interpolation config — per entity type, live-tunable, presets.
 *
 * The SnapshotInterpolator smooths remote entities (mobs + remote players).
 * Mobs and remote players have very different source rates (mobs ~60Hz server
 * tick; players ~20Hz position claims), so they get separate configs. DJ wants
 * the feel "more configurable via gui and presets and diagnostics and
 * launching" — this is the single tunable surface:
 *   - defaults biased crisp/immediate (2D platformer — remote-motion latency
 *     reads against fixed ground/tile edges, unlike 3D)
 *   - named presets (maple-snappy / wow-smooth / linear-baseline)
 *   - a runtime store the tuning tab mutates; a version counter lets the
 *     managers re-apply to live interpolators without a reload (live A/B)
 *   - launch-time URL seeds (?interp=, ?playerLag=, ?mobLag=)
 *
 * Lives under game/ (not shared/config) because remote-entity interpolation is
 * purely client-side. snapDistanceSq stays in network.config.ts (shared).
 */

import { browserOnly } from '../../../shared/util/browserOnly';
import type { InterpolationConfig, InterpMode } from './SnapshotInterpolator';
import { bootSearchParams } from '../../../config/boot/bootUrl';

export type InterpEntityType = 'player' | 'mob';

// Crisp/immediate defaults (DJ: "crisp and immediate is needed for a 2d game").
// Players update at ~20Hz so carry a hair more render-lag than mobs (~60Hz);
// both Hermite with tight tangents (minimal overshoot).
//
// LANE-6 HYPOTHESIS (2026-06-03, pending 2-real-client verify): remote players
// look choppy because 90ms render-lag over a 20Hz source (~50ms claim spacing)
// buys only ~1.8 ticks of buffer — a single late claim arrival under-buffers
// and forces the interpolator to extrapolate, which reads as a hitch. Deepened
// to 120ms (~2.4 ticks) to absorb claim-arrival jitter. This is the LOWER-RISK
// lever vs raising the claim rate (POSITION.claimIntervalMs in network.config):
// purely client-side, zero added wire cost, A/B-able live via `?playerLag=` /
// the Interp Tuning tab, and reverted by flipping the one value below.
//   ONE-LINE REVERT: renderLagMs: 90
export const PLAYER_INTERP_DEFAULT: InterpolationConfig = {
  renderLagMs: 120, hitchTimeoutMs: 200, ringCapacity: 8, mode: 'hermite', tangentClampFactor: 2,
};
export const MOB_INTERP_DEFAULT: InterpolationConfig = {
  renderLagMs: 85, hitchTimeoutMs: 200, ringCapacity: 8, mode: 'hermite', tangentClampFactor: 2,
};

export interface InterpPreset {
  id: string;
  label: string;
  description: string;
  player: Partial<InterpolationConfig>;
  mob: Partial<InterpolationConfig>;
}

export const INTERP_PRESETS: readonly InterpPreset[] = [
  {
    id: 'maple-snappy',
    label: 'MapleStory (crisp)',
    description: 'Shallow render-lag, immediate. The 2D default.',
    player: { renderLagMs: 90, mode: 'hermite', tangentClampFactor: 2 },
    mob: { renderLagMs: 85, mode: 'hermite', tangentClampFactor: 2 },
  },
  {
    id: 'wow-smooth',
    label: 'WoW (buttery)',
    description: 'Deeper render-lag, fluid, tolerant of jitter (more latency).',
    player: { renderLagMs: 140, mode: 'hermite', tangentClampFactor: 3 },
    mob: { renderLagMs: 110, mode: 'hermite', tangentClampFactor: 3 },
  },
  {
    id: 'linear-baseline',
    label: 'Linear (baseline)',
    description: 'Pre-Hermite C0 — rollback + A/B reference.',
    player: { mode: 'linear' },
    mob: { mode: 'linear' },
  },
];

class InterpRuntimeConfigStore {
  player: InterpolationConfig = { ...PLAYER_INTERP_DEFAULT };
  mob: InterpolationConfig = { ...MOB_INTERP_DEFAULT };
  /** Bumped on every mutation. Managers compare it to re-apply to live
   *  interpolators (O(1) check per frame; O(entities) only on actual change). */
  version = 0;

  constructor() {
    browserOnly(() => this.seedFromUrl());
  }

  get(type: InterpEntityType): InterpolationConfig {
    return type === 'player' ? this.player : this.mob;
  }

  set(type: InterpEntityType, partial: Partial<InterpolationConfig>): void {
    Object.assign(this.get(type), partial);
    this.version++;
  }

  applyPreset(id: string): boolean {
    const preset = INTERP_PRESETS.find((p) => p.id === id);
    if (!preset) return false;
    Object.assign(this.player, preset.player);
    Object.assign(this.mob, preset.mob);
    this.version++;
    return true;
  }

  reset(): void {
    this.player = { ...PLAYER_INTERP_DEFAULT };
    this.mob = { ...MOB_INTERP_DEFAULT };
    this.version++;
  }

  /** Launch-time URL seeds. ?interp=linear|hermite sets both modes;
   *  ?playerLag / ?mobLag set render-lag (ms). Touches version once. */
  seedFromUrl(): void {
    try {
      const sp = bootSearchParams();
      let touched = false;
      const mode = sp.get('interp');
      if (mode === 'linear' || mode === 'hermite') {
        this.player.mode = mode as InterpMode;
        this.mob.mode = mode as InterpMode;
        touched = true;
      }
      const pl = Number(sp.get('playerLag'));
      if (Number.isFinite(pl) && pl >= 0 && sp.has('playerLag')) { this.player.renderLagMs = pl; touched = true; }
      const ml = Number(sp.get('mobLag'));
      if (Number.isFinite(ml) && ml >= 0 && sp.has('mobLag')) { this.mob.renderLagMs = ml; touched = true; }
      if (touched) this.version++;
    } catch { /* ignore */ }
  }
}

export const interpRuntimeConfig = new InterpRuntimeConfigStore();

if (typeof window !== 'undefined') {
  (window as any).__INTERP_CONFIG__ = interpRuntimeConfig;
}
